import React, { useState, useEffect } from "react";
import axios from "axios";
import { Handshake, Plus, Pencil, Trash2, Search, X, Loader2, CheckCircle2, AlertCircle, MapPin, History, Wallet, Printer, TrendingDown, TrendingUp, ArrowDownCircle, ArrowUpCircle, Percent } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY = {
  nama: "",
  nik: "",
  no_hp: "",
  alamat: "",
  tanggal_bergabung: new Date().toISOString().slice(0, 10),
  jumlah_modal: 0,
  wilayah_ids: [],
  prosentase_sharing: 0,
  status: "aktif",
  catatan: "",
  akun_setor_id: "",
};

const MitraForm = ({ initial, wilayahList, akunList, onClose, onSuccess }) => {
  const kasBankOnly = (akunList || []).filter((a) => a.tipe === "tunai" || a.tipe === "bank");
  const [form, setForm] = useState(initial
    ? { ...initial, wilayah_ids: initial.wilayah_ids || [], akun_setor_id: initial.akun_setor_id || (kasBankOnly[0]?.id || "") }
    : { ...EMPTY, akun_setor_id: kasBankOnly[0]?.id || "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmSync, setConfirmSync] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isEdit = !!initial?.id;
  const affectedCount = initial?.pelanggan_count || 0;
  const persenChanged = isEdit && parseFloat(form.prosentase_sharing) !== parseFloat(initial?.prosentase_sharing ?? 0);
  const modalDelta = isEdit ? (parseFloat(form.jumlah_modal) || 0) - (parseFloat(initial?.jumlah_modal) || 0) : 0;
  const modalChanged = isEdit && Math.abs(modalDelta) > 0.0001;
  const nextMonthLabel = (() => {
    const d = new Date(); d.setMonth(d.getMonth() + 1);
    return d.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  })();

  const toggleWilayah = (id) => {
    setForm((f) => ({
      ...f,
      wilayah_ids: f.wilayah_ids.includes(id) ? f.wilayah_ids.filter((x) => x !== id) : [...f.wilayah_ids, id],
    }));
  };

  const doSave = async () => {
    setError(""); setLoading(true);
    try {
      const payload = { ...form, jumlah_modal: parseFloat(form.jumlah_modal), prosentase_sharing: parseFloat(form.prosentase_sharing) };
      if (initial?.id) await axios.put(`${BACKEND_URL}/api/master/mitra/${initial.id}`, payload, { withCredentials: true });
      else await axios.post(`${BACKEND_URL}/api/master/mitra`, payload, { withCredentials: true });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal simpan mitra"); setConfirmSync(false); } finally { setLoading(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isEdit && (persenChanged || modalChanged)) { setConfirmSync(true); return; }
    doSave();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-[#0e1628] z-10">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><Handshake className="w-4 h-4 text-amber-400" />{initial?.id ? "Edit Mitra/Investor" : "Tambah Mitra/Investor"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {confirmSync ? (
          <div className="p-5 space-y-4" data-testid="mitra-sync-confirm">
            <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-amber-200 text-xs space-y-2">
              <p className="font-bold text-amber-100 flex items-center gap-2"><AlertCircle className="w-4 h-4" />Konfirmasi Perubahan Investor</p>
              {persenChanged && (
                <div className="space-y-0.5">
                  <p>Persentase bagi hasil: <b className="text-white">{Number(initial?.prosentase_sharing || 0)}%</b> → <b className="text-white">{Number(form.prosentase_sharing || 0)}%</b></p>
                  <p className="text-[11px] text-amber-300/90">Disinkronkan ke <b className="text-white">{affectedCount} pelanggan</b> yang tertaut ke investor ini.</p>
                  <p className="text-[11px] text-emerald-300/90">Berlaku untuk bagi hasil mulai <b className="text-white">{nextMonthLabel}</b>. Bagi hasil bulan-bulan sebelumnya TIDAK berubah.</p>
                </div>
              )}
              {modalChanged && (
                <div className="space-y-0.5 pt-1 border-t border-amber-500/20">
                  <p>Modal: <b className="text-white">Rp {Number(initial?.jumlah_modal || 0).toLocaleString("id-ID")}</b> → <b className="text-white">Rp {Number(form.jumlah_modal || 0).toLocaleString("id-ID")}</b></p>
                  <p className="text-[11px] text-amber-300/90">{modalDelta > 0 ? "Tambahan" : "Pengurangan"} <b className="text-white">Rp {Math.abs(modalDelta).toLocaleString("id-ID")}</b> dicatat sebagai {modalDelta > 0 ? "setoran modal" : "penarikan modal"} baru bertanggal hari ini. Modal & Neraca bulan lalu tidak berubah.</p>
                </div>
              )}
            </div>
            {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
            <div className="flex gap-2">
              <button type="button" onClick={() => setConfirmSync(false)} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700" data-testid="mitra-sync-cancel-btn">Batal</button>
              <button type="button" onClick={doSave} disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-orange-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="mitra-sync-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Ya, Simpan & Sinkronkan
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Nama Mitra</label>
            <input required type="text" value={form.nama} onChange={(e) => setField("nama", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="mitra-nama-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">NIK</label>
              <input required type="text" value={form.nik} onChange={(e) => setField("nik", e.target.value)} placeholder="16 digit" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="mitra-nik-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">No HP</label>
              <input required type="text" value={form.no_hp} onChange={(e) => setField("no_hp", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="mitra-hp-input" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Alamat</label>
            <textarea required rows={2} value={form.alamat} onChange={(e) => setField("alamat", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="mitra-alamat-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Tanggal Bergabung</label>
              <input required type="date" value={form.tanggal_bergabung} onChange={(e) => setField("tanggal_bergabung", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="mitra-tanggal-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1">Prosentase Sharing (%)</label>
              <input required type="number" min={0} max={100} step="0.1" value={form.prosentase_sharing} onChange={(e) => setField("prosentase_sharing", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="mitra-sharing-input" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Jumlah Modal (Rp)</label>
            <input required type="number" min={0} value={form.jumlah_modal} onChange={(e) => setField("jumlah_modal", e.target.value)} placeholder="50000000" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="mitra-modal-input" />
            {form.jumlah_modal > 0 && (
              <p className="text-[10px] text-emerald-400 mt-1 font-mono">Rp {Number(form.jumlah_modal).toLocaleString("id-ID")}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Setor Modal ke Akun (Kas/Bank)</label>
            <select value={form.akun_setor_id} onChange={(e) => setField("akun_setor_id", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="mitra-akun-setor-select">
              <option value="">-- Tidak disinkron ke kas --</option>
              {kasBankOnly.map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
            </select>
            <p className="text-[10px] text-cyan-400/80 mt-1">Nominal modal otomatis tercatat sebagai "Modal Disetor" (Uang Masuk) di akun ini. Saat modal diubah, hanya SELISIH-nya yang dicatat sebagai setoran baru bertanggal — modal & Neraca bulan lalu tidak berubah.</p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-purple-400" />Wilayah (pilih 1 atau lebih)</label>
            <div className="p-2 bg-slate-900 border border-slate-700 rounded-xl max-h-40 overflow-y-auto space-y-1" data-testid="mitra-wilayah-list">
              {wilayahList.length === 0 && <p className="text-[11px] text-slate-500 italic p-2">Belum ada wilayah. Buat di menu Data Wilayah dulu.</p>}
              {wilayahList.map((w) => (
                <label key={w.id} className="flex items-center gap-2 p-1.5 rounded-md hover:bg-slate-800 cursor-pointer">
                  <input type="checkbox" checked={form.wilayah_ids.includes(w.id)} onChange={() => toggleWilayah(w.id)} className="w-3.5 h-3.5 accent-amber-500" data-testid={`mitra-wilayah-checkbox-${w.id}`} />
                  <span className="text-[11px] font-mono text-purple-300">{w.kode_wilayah}</span>
                  <span className="text-xs text-slate-200 flex-1">{w.nama_wilayah}</span>
                  <span className="text-[10px] text-slate-500">{w.kecamatan}, {w.kabupaten}</span>
                </label>
              ))}
            </div>
            {form.wilayah_ids.length > 0 && (
              <p className="text-[10px] text-amber-400 mt-1">Terpilih: {form.wilayah_ids.length} wilayah</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Status</label>
            <select value={form.status} onChange={(e) => setField("status", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
              <option value="aktif">Aktif</option>
              <option value="nonaktif">Nonaktif</option>
              <option value="tarik_modal">Tarik Modal</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Catatan</label>
            <input type="text" value={form.catatan} onChange={(e) => setField("catatan", e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>

          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="submit" disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-orange-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="mitra-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {initial?.id ? "Update" : "Simpan"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

const monthOptionsList = () => {
  const now = new Date();
  const arr = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push({ kode: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleDateString("id-ID", { month: "long", year: "numeric" }) });
  }
  return arr;
};

const rpx = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const printSlipBagiHasil = (slip, brand) => {
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const perus = brand?.perusahaan || {};
  const namaPerus = perus.nama_isp || brand?.app_name || "PERUSAHAAN";
  const logo = brand?.app_logo || "";
  const periodeLabel = monthOptionsList().find((o) => o.kode === slip.periode)?.label || slip.periode;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Slip Bagi Hasil ${esc(slip.nama)}</title>
  <style>
    *{box-sizing:border-box}body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:24px}
    .head{display:flex;align-items:center;gap:14px;border-bottom:3px double #111;padding-bottom:10px;margin-bottom:6px}
    .head img{height:54px;width:auto}.head h1{font-size:18px;margin:0}.head p{font-size:11px;margin:2px 0 0;color:#444}
    .title{text-align:center;margin:10px 0 14px}.title h2{font-size:15px;margin:0;text-transform:uppercase;letter-spacing:.5px}
    .title p{font-size:11px;color:#555;margin:2px 0 0}
    table{width:100%;border-collapse:collapse;font-size:12px;margin:8px 0}
    th,td{border:1px solid #333;padding:7px 9px}td.r{text-align:right;font-family:'Courier New',monospace;white-space:nowrap}
    tr.total td{background:#f4f4f4;font-weight:bold}
    .box{border:1px solid #333;padding:10px 12px;margin:8px 0;font-size:12px}
    .foot{margin-top:40px;font-size:11px;display:flex;justify-content:space-between}
    .sign{text-align:center;width:220px}.sign .line{margin-top:60px;border-top:1px solid #111;padding-top:4px}
    @media print{body{margin:12mm}}
  </style></head><body>
    <div class="head">${logo ? `<img src="${esc(logo)}" alt="logo"/>` : ""}
      <div><h1>${esc(namaPerus)}</h1>${perus.alamat ? `<p>${esc(perus.alamat)}</p>` : ""}${(perus.telepon || perus.whatsapp) ? `<p>Telp/WA: ${esc(perus.telepon || perus.whatsapp)}</p>` : ""}</div>
    </div>
    <div class="title"><h2>Slip Bagi Hasil Investor</h2><p>Periode ${esc(periodeLabel)} · Dicetak ${new Date().toLocaleString("id-ID")}</p></div>
    <div class="box"><b>Nama Investor:</b> ${esc(slip.nama)}<br/><b>Modal Ditanamkan:</b> ${rpx(slip.modal)}<br/><b>Persentase Bagi Hasil:</b> ${Number(slip.prosentase_sharing).toFixed(1)}%<br/><b>Jumlah Pelanggan Terkait:</b> ${slip.jumlah_pelanggan}</div>
    <table>
      <tr><td>Pendapatan Terkait Investor (periode)</td><td class="r">${rpx(slip.pendapatan_terkait)}</td></tr>
      <tr><td>Sisa Profit Perusahaan Siap Dibagi</td><td class="r">${rpx(slip.sisa_profit_perusahaan)}</td></tr>
      <tr class="total"><td>BAGIAN BAGI HASIL INVESTOR</td><td class="r">${rpx(slip.bagian)}</td></tr>
    </table>
    <p style="font-size:11px;color:#555">Bagian dihitung proporsional atas sisa profit perusahaan sesuai porsi kepemilikan pelanggan &amp; persentase bagi hasil yang berlaku pada periode ini.</p>
    <div class="foot">
      <div class="sign"><div>Penerima,</div><div class="line">( ${esc(slip.nama)} )</div></div>
      <div class="sign"><div>Mengetahui / Pimpinan,</div><div class="line">( ............................... )</div></div>
    </div>
    <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { alert("Popup diblokir browser. Izinkan popup untuk mencetak."); return; }
  w.document.open(); w.document.write(html); w.document.close();
};

const TarikModalForm = ({ mitra, akunList, onClose, onSuccess }) => {
  const kasBankOnly = (akunList || []).filter((a) => a.tipe === "tunai" || a.tipe === "bank");
  const [nominal, setNominal] = useState("");
  const [akunId, setAkunId] = useState(kasBankOnly[0]?.id || "");
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));
  const [keterangan, setKeterangan] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    const n = parseFloat(nominal);
    if (!n || n <= 0) { setError("Nominal harus lebih dari 0"); return; }
    if (n > (mitra.jumlah_modal || 0)) { setError(`Nominal melebihi modal tersedia (${rpx(mitra.jumlah_modal)})`); return; }
    if (!akunId) { setError("Pilih akun kas/bank sumber"); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/master/mitra/${mitra.id}/tarik-modal`,
        { nominal: n, akun_id: akunId, tanggal, keterangan }, { withCredentials: true });
      onSuccess(); onClose();
    } catch (err) { setError(err.response?.data?.detail || "Gagal tarik modal"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl" data-testid="tarik-modal-form">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><TrendingDown className="w-4 h-4 text-rose-400" />Tarik Modal — {mitra.nama}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-200">Modal tersedia: <b className="font-mono">{rpx(mitra.jumlah_modal)}</b></div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Nominal Penarikan (Rp)</label>
            <input type="number" min={0} value={nominal} onChange={(e) => setNominal(e.target.value)} placeholder="10000000" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="tarik-nominal-input" />
            {nominal > 0 && <p className="text-[10px] text-rose-400 mt-1 font-mono">{rpx(nominal)}</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Ambil dari Akun (Kas/Bank)</label>
            <select value={akunId} onChange={(e) => setAkunId(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="tarik-akun-select">
              {kasBankOnly.map((a) => <option key={a.id} value={a.id}>{a.nama}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Tanggal Penarikan</label>
            <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono" data-testid="tarik-tanggal-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1">Keterangan (opsional)</label>
            <input type="text" value={keterangan} onChange={(e) => setKeterangan(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>
          <p className="text-[10px] text-slate-400">Dicatat sebagai pengeluaran "Penarikan Modal" di kas & mengurangi modal investor. Persentase bagi hasil tidak berubah.</p>
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button type="button" onClick={onClose} className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700">Batal</button>
            <button type="button" onClick={submit} disabled={loading} className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-rose-600 to-red-600 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="tarik-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingDown className="w-4 h-4" />}Tarik Modal
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const RiwayatModal = ({ mitra, brand, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState(monthOptionsList()[0].kode);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/master/mitra/${mitra.id}/riwayat`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [mitra.id]);

  const doPrint = async () => {
    setPrinting(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/master/mitra/${mitra.id}/bagi-hasil?periode=${periode}`, { withCredentials: true });
      printSlipBagiHasil(r.data, brand);
    } catch (err) { alert(err.response?.data?.detail || "Gagal ambil data bagi hasil"); } finally { setPrinting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl max-h-[92vh] overflow-y-auto" data-testid="mitra-riwayat-modal">
        <div className="p-4 border-b border-slate-800 flex justify-between items-center sticky top-0 bg-[#0e1628] z-10">
          <h2 className="text-base font-bold text-white flex items-center gap-2"><History className="w-4 h-4 text-cyan-400" />Riwayat & Audit — {mitra.nama}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          {/* Cetak Slip Bagi Hasil */}
          <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
            <p className="text-xs font-bold text-indigo-200 mb-2 flex items-center gap-1.5"><Printer className="w-3.5 h-3.5" />Cetak Slip Bagi Hasil</p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex-1 min-w-[160px]">
                <label className="text-[10px] text-slate-400 block mb-1">Periode</label>
                <select value={periode} onChange={(e) => setPeriode(e.target.value)} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="riwayat-periode-select">
                  {monthOptionsList().map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
                </select>
              </div>
              <button onClick={doPrint} disabled={printing} className="px-4 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center gap-2 disabled:opacity-50" data-testid="print-slip-btn">
                {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}Cetak Slip
              </button>
            </div>
          </div>

          {loading ? <p className="text-slate-500 text-xs text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat riwayat...</p> : data && (
            <>
              {/* Timeline Persentase */}
              <div>
                <p className="text-xs font-bold text-amber-300 mb-2 flex items-center gap-1.5"><Percent className="w-3.5 h-3.5" />Riwayat Persentase Bagi Hasil</p>
                <div className="space-y-2" data-testid="persen-timeline">
                  {data.persen_timeline.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                      <div className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                      <span className="text-xs text-slate-300">Berlaku <b className="text-white">{h.efektif_periode}</b></span>
                      <span className="ml-auto font-mono font-bold text-amber-300 text-sm">{Number(h.prosentase_sharing).toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline Modal */}
              <div>
                <p className="text-xs font-bold text-emerald-300 mb-2 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" />Riwayat Transaksi Modal</p>
                <div className="space-y-2" data-testid="modal-timeline">
                  {data.modal_transaksi.length === 0 && <p className="text-slate-500 italic text-xs">Belum ada transaksi modal tercatat.</p>}
                  {data.modal_transaksi.map((t, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-900 border border-slate-800">
                      {t.jenis === "setoran"
                        ? <ArrowUpCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                        : <ArrowDownCircle className="w-4 h-4 text-rose-400 shrink-0" />}
                      <div className="min-w-0">
                        <p className="text-xs text-slate-200 font-semibold">{t.jenis === "setoran" ? "Setoran Modal" : "Penarikan Modal"}</p>
                        <p className="text-[10px] text-slate-500 truncate">{t.tanggal} · {t.akun}{t.keterangan ? ` · ${t.keterangan}` : ""}</p>
                      </div>
                      <span className={`ml-auto font-mono font-bold text-sm ${t.jenis === "setoran" ? "text-emerald-300" : "text-rose-300"}`}>{t.jenis === "setoran" ? "+" : "−"}{rpx(t.nominal)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex justify-between items-center">
                  <span className="text-xs text-emerald-200 font-semibold">Modal Saat Ini</span>
                  <span className="font-mono font-bold text-emerald-300">{rpx(data.modal_sekarang)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const InvestorDashboard = () => {
  const [data, setData] = useState(null);
  const [periode, setPeriode] = useState(monthOptionsList()[0].kode);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/master/mitra/dashboard?periode=${periode}`, { withCredentials: true })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [periode]);

  const periodeLabel = monthOptionsList().find((o) => o.kode === periode)?.label || periode;

  return (
    <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl" data-testid="investor-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-bold text-white flex items-center gap-2"><TrendingUp className="w-4 h-4 text-cyan-400" />Dashboard Investor <span className="text-[11px] font-normal text-slate-500">· {periodeLabel}</span></h2>
        <select value={periode} onChange={(e) => setPeriode(e.target.value)} className="px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" data-testid="dashboard-periode-select">
          {monthOptionsList().map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
        </select>
      </div>
      {loading ? <p className="text-slate-500 text-xs text-center py-6"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat dashboard...</p> : !data || data.cards.length === 0 ? (
        <p className="text-slate-500 italic text-xs text-center py-6">Belum ada investor.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800"><p className="text-[10px] text-slate-400">Total Modal</p><p className="text-sm font-bold text-emerald-400 font-mono">{rpx(data.total_modal)}</p></div>
            <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800"><p className="text-[10px] text-slate-400">Bagi Hasil Investor ({periodeLabel})</p><p className="text-sm font-bold text-amber-300 font-mono">{rpx(data.total_bagi_hasil)}</p></div>
            <div className="p-2.5 rounded-xl bg-slate-950/60 border border-slate-800"><p className="text-[10px] text-slate-400">Bagian Perusahaan</p><p className="text-sm font-bold text-cyan-300 font-mono">{rpx(data.bagian_perusahaan)}</p></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="investor-cards">
            {data.cards.map((c) => {
              const maxT = Math.max(...c.trend.map((t) => t.bagian), 1);
              return (
                <div key={c.id} className="p-4 rounded-2xl bg-gradient-to-br from-slate-900 to-[#0b1220] border border-slate-800 shadow-lg" data-testid={`investor-card-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-white truncate">{c.nama}</h3>
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${c.status === "aktif" ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>{(c.status || "").toUpperCase().replace("_", " ")}</span>
                    </div>
                    <span className="font-mono font-bold text-amber-300 text-xs shrink-0">{Number(c.prosentase_sharing).toFixed(1)}%</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div><p className="text-[9px] text-slate-500">Modal</p><p className="text-xs font-mono font-bold text-emerald-300">{rpx(c.modal)}</p></div>
                    <div><p className="text-[9px] text-slate-500">Pelanggan</p><p className="text-xs font-mono font-bold text-cyan-300">{c.jumlah_pelanggan}</p></div>
                  </div>
                  <div className="mt-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/25">
                    <p className="text-[9px] text-amber-400">Bagi Hasil {periodeLabel}</p>
                    <p className="text-base font-black font-mono text-amber-300">{rpx(c.bagian_bulan_ini)}</p>
                  </div>
                  <div className="mt-3">
                    <p className="text-[9px] text-slate-500 mb-1">Tren 6 Bulan</p>
                    <div className="flex items-end gap-1 h-12">
                      {c.trend.map((t, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-0.5 group relative">
                          <div className="w-full bg-cyan-500/60 rounded-t hover:bg-cyan-400 transition-colors" style={{ height: `${Math.max((t.bagian / maxT) * 40, 2)}px` }} title={`${t.periode}: ${rpx(t.bagian)}`} />
                          <span className="text-[7px] text-slate-600">{t.periode.slice(5)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export const MasterMitraPage = () => {
  const [items, setItems] = useState([]);
  const [wilayahList, setWilayahList] = useState([]);
  const [akunList, setAkunList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [riwayatMitra, setRiwayatMitra] = useState(null);
  const [tarikMitra, setTarikMitra] = useState(null);
  const [brand, setBrand] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [mitraRes, wilRes, akunRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/master/mitra`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/kas/summary`, { withCredentials: true }),
      ]);
      setItems(mitraRes.data);
      setWilayahList(wilRes.data);
      setAkunList(akunRes.data?.akun || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/branding`, { withCredentials: true }).then((r) => setBrand(r.data || {})).catch(() => {});
  }, []);

  const handleDelete = async (id, nama) => {
    if (!window.confirm(`Hapus mitra "${nama}"?`)) return;
    try { await axios.delete(`${BACKEND_URL}/api/master/mitra/${id}`, { withCredentials: true }); load(); }
    catch (err) { alert(err.response?.data?.detail || "Gagal hapus"); }
  };

  const filtered = items.filter((m) => !search ||
    m.nama?.toLowerCase().includes(search.toLowerCase()) ||
    m.nik?.includes(search) ||
    m.no_hp?.includes(search)
  );

  const totalModal = items.reduce((s, m) => s + (m.jumlah_modal || 0), 0);
  const totalSharing = items.reduce((s, m) => s + (m.prosentase_sharing || 0), 0);

  return (
    <div className="space-y-4" data-testid="mitra-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold uppercase">Master Data</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Data Mitra / Investor</h1>
          <p className="text-xs text-slate-400 mt-0.5">Kelola investor pemasok modal & pembagian keuntungan.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-amber-600/30 flex items-center gap-2" data-testid="add-mitra-btn">
          <Plus className="w-4 h-4" />Tambah Mitra
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Total Mitra Aktif</p>
          <p className="text-lg font-bold text-amber-400 font-mono">{items.filter(m => m.status === "aktif").length}</p>
        </div>
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Total Modal Terkumpul</p>
          <p className="text-lg font-bold text-emerald-400 font-mono">Rp {totalModal.toLocaleString("id-ID")}</p>
        </div>
        <div className="p-3 rounded-xl bg-[#0e1628] border border-slate-800">
          <p className="text-[10px] text-slate-400">Total Sharing</p>
          <p className={`text-lg font-bold font-mono ${totalSharing > 100 ? "text-rose-400" : "text-cyan-400"}`}>{totalSharing.toFixed(1)}%</p>
        </div>
      </div>

      <InvestorDashboard />

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama/NIK/HP..." className="w-full pl-9 pr-3 py-1.5 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200" />
          </div>
          <span className="text-xs text-slate-500">Total: <strong className="text-amber-400">{filtered.length}</strong></span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="mitra-table">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400">
                <th className="py-2.5 px-3">Nama</th>
                <th className="py-2.5 px-3">NIK / HP</th>
                <th className="py-2.5 px-3">Bergabung</th>
                <th className="py-2.5 px-3 text-right">Modal</th>
                <th className="py-2.5 px-3 text-center">Sharing</th>
                <th className="py-2.5 px-3">Wilayah Terhubung</th>
                <th className="py-2.5 px-3 text-center">Status</th>
                <th className="py-2.5 px-3 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {loading ? (
                <tr><td colSpan={8} className="py-6 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Memuat...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-6 text-center text-slate-500 italic">Belum ada mitra</td></tr>
              ) : filtered.map((m) => (
                <tr key={m.id} className="hover:bg-slate-900/40" data-testid={`mitra-row-${m.id}`}>
                  <td className="py-2.5 px-3 font-semibold text-white">{m.nama}<div className="text-[10px] text-slate-500 font-normal truncate max-w-[200px]">{m.alamat}</div></td>
                  <td className="py-2.5 px-3 font-mono text-slate-300 text-[11px]">{m.nik}<div className="text-[10px] text-slate-500">{m.no_hp}</div></td>
                  <td className="py-2.5 px-3 font-mono text-slate-400">{m.tanggal_bergabung}</td>
                  <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-400">Rp {Number(m.jumlah_modal).toLocaleString("id-ID")}</td>
                  <td className="py-2.5 px-3 text-center"><span className="font-mono font-bold text-amber-300">{Number(m.prosentase_sharing).toFixed(1)}%</span></td>
                  <td className="py-2.5 px-3">
                    <div className="flex flex-wrap gap-1 max-w-[250px]">
                      {(m.wilayah_details || []).map((w) => (
                        <span key={w.id} className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 text-[10px] font-mono">{w.kode_wilayah}</span>
                      ))}
                      {(m.wilayah_details || []).length === 0 && <span className="text-slate-500 italic text-[10px]">Semua wilayah</span>}
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      m.status === "aktif" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" :
                      m.status === "nonaktif" ? "bg-slate-800 text-slate-400 border border-slate-700" :
                      "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                    }`}>{(m.status || "").toUpperCase().replace("_", " ")}</span>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setRiwayatMitra(m)} className="p-1 rounded bg-slate-800 hover:bg-cyan-900/60 text-cyan-400" title="Riwayat & Cetak Slip" data-testid={`riwayat-mitra-${m.id}`}><History className="w-3 h-3" /></button>
                      <button onClick={() => setTarikMitra(m)} className="p-1 rounded bg-slate-800 hover:bg-rose-900/60 text-rose-400" title="Tarik Modal" data-testid={`tarik-mitra-${m.id}`}><TrendingDown className="w-3 h-3" /></button>
                      <button onClick={() => { setEditing(m); setShowForm(true); }} className="p-1 rounded bg-slate-800 hover:bg-amber-900/60 text-amber-400" data-testid={`edit-mitra-${m.id}`}><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => handleDelete(m.id, m.nama)} className="p-1 rounded bg-slate-800 hover:bg-rose-900/60 text-rose-400" data-testid={`delete-mitra-${m.id}`}><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && <MitraForm initial={editing} wilayahList={wilayahList} akunList={akunList} onClose={() => setShowForm(false)} onSuccess={load} />}
      {riwayatMitra && <RiwayatModal mitra={riwayatMitra} brand={brand} onClose={() => setRiwayatMitra(null)} />}
      {tarikMitra && <TarikModalForm mitra={tarikMitra} akunList={akunList} onClose={() => setTarikMitra(null)} onSuccess={load} />}
    </div>
  );
};
